import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'

const API = import.meta.env.VITE_API_URL || 'http://localhost:4000'
const COURSE_ID = 'ml-foundations'

const weeks = [
  { week: 1, topic: 'Introduction to Machine Learning & Basics' },
  { week: 2, topic: 'Calculus Basics' },
  { week: 3, topic: 'Linear Algebra & Fundamental Subspaces' },
  { week: 4, topic: 'Linear/Polynomial Regression & Eigenvalues/Eigenvectors' },
]

const TOTAL_WEEKS = 12

const MLFoundations = () => {
  const [user, setUser] = useState(null)
  const [attempts, setAttempts] = useState([])
  const [loading, setLoading] = useState(true)
  const [attemptsError, setAttemptsError] = useState(false)

  useEffect(() => {
    const loadProgress = async () => {
      try {
        const sessionResponse = await axios.get(`${API}/api/session-info`, { withCredentials: true })
        const sessionUser = sessionResponse.data
        if (!sessionUser?.email) return

        setUser(sessionUser)
        try {
          const attemptsResponse = await axios.get(
            `${API}/api/mcq-quiz/attempts?email=${encodeURIComponent(sessionUser.email)}&course=${COURSE_ID}`,
            { withCredentials: true }
          )
          setAttempts(attemptsResponse.data?.attempts || [])
        } catch (error) {
          console.error('Failed to load ML Foundations quiz progress:', error)
          setAttemptsError(true)
        }
      } catch {
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    loadProgress()
  }, [])

  const attemptsForWeek = (week) => attempts.filter((attempt) => attempt.week === week)
  const bestAttemptForWeek = (week) => {
    const weekAttempts = attemptsForWeek(week)
    return weekAttempts.reduce(
      (best, attempt) => !best || attempt.percentage > best.percentage ? attempt : best,
      null
    )
  }

  const weeksAttempted = weeks.filter((item) => bestAttemptForWeek(item.week)).length
  const averageScore = attempts.length
    ? Math.round(attempts.reduce((sum, attempt) => sum + (attempt.percentage || 0), 0) / attempts.length)
    : 0
  const bestScore = attempts.length
    ? Math.round(Math.max(...attempts.map((attempt) => attempt.percentage || 0)))
    : 0

  const stats = [
    { label: 'Weeks Attempted', value: `${weeksAttempted} / ${TOTAL_WEEKS}`, icon: 'bi-journals', color: '#0d6efd' },
    { label: 'Total Attempts', value: attempts.length, icon: 'bi-arrow-repeat', color: '#6f42c1' },
    { label: 'Average Score', value: `${averageScore}%`, icon: 'bi-bar-chart-line', color: '#6c757d' },
    { label: 'Best Score', value: `${bestScore}%`, icon: 'bi-trophy-fill', color: '#6c757d' },
  ]

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading course...</span>
        </div>
      </div>
    )
  }

  return (
    <main className="main">
      <div className="page-title" data-aos="fade">
        <div className="heading">
          <div className="container">
            <div className="row justify-content-center text-center">
              <div className="col-lg-8">
                <div className="d-flex align-items-center justify-content-center gap-3 mb-2">
                  <i className="bi bi-cpu-fill fs-2" style={{ color: '#7a62a8' }} aria-hidden="true"></i>
                  <h1 className="mb-0">ML Foundations</h1>
                </div>
                <p className="mb-0">
                  Build your machine-learning foundation through weekly topics and MCQ quizzes.
                  Weeks 1–4 are available now.
                </p>
              </div>
            </div>
          </div>
        </div>
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <div className="container">
            <ol>
              <li><Link to="/">Home</Link></li>
              <li>Data Science</li>
              <li className="current">ML Foundations</li>
            </ol>
          </div>
        </nav>
      </div>

      <section className="section pt-4">
        <div className="container">
          {!user && (
            <div className="alert alert-warning d-flex align-items-center gap-2 mb-4">
              <i className="bi bi-exclamation-triangle-fill fs-5" aria-hidden="true"></i>
              <span>
                <Link to="/login" className="alert-link">Log in</Link> to take quizzes, save your scores, and track progress.
              </span>
            </div>
          )}

          {attemptsError && (
            <div className="alert alert-danger" role="alert">
              Couldn’t load your ML Foundations quiz progress. You can still take an available quiz.
            </div>
          )}

          {user && (
            <div className="row g-3 mb-4 mx-auto" style={{ maxWidth: 860 }}>
              {stats.map((stat) => (
                <div className="col-6 col-md-3" key={stat.label}>
                  <div className="card border-0 shadow-sm text-center h-100" style={{ borderRadius: 14 }}>
                    <div className="card-body py-3 px-2">
                      <i className={`bi ${stat.icon} fs-4`} style={{ color: stat.color }} aria-hidden="true"></i>
                      <div className="fs-5 fw-bold mt-1" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-muted small">{stat.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mx-auto" style={{ maxWidth: 860 }}>
            {weeks.map((item) => {
              const bestAttempt = bestAttemptForWeek(item.week)
              const ready = item.week <= 4
              const quizUrl = `/quiz/${COURSE_ID}/week/${item.week}?question_type=mcq`

              return (
                <article className="card border-0 shadow-sm mb-3" key={item.week} style={{ borderRadius: 14 }}>
                  <div className="card-body p-3 p-md-4">
                    <div className="row align-items-center g-3">
                      <div className="col-auto">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                          style={{
                            width: 44,
                            height: 44,
                            background: bestAttempt ? '#7a62a8' : ready ? '#ede8f5' : '#e9ecef',
                            color: bestAttempt ? '#fff' : ready ? '#7a62a8' : '#6c757d',
                          }}
                        >
                          {bestAttempt ? <i className="bi bi-check-lg fs-5" aria-label="Attempted"></i> : item.week}
                        </div>
                      </div>

                      <div className="col">
                        <h2 className="h6 fw-semibold mb-1">Week {item.week}: {item.topic}</h2>
                        {ready ? (
                          <>
                            {bestAttempt ? (
                              <>
                                <div className="progress mb-1" style={{ height: 5 }}>
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    aria-label={`Best score ${Math.round(bestAttempt.percentage)} percent`}
                                    style={{
                                      width: `${Math.max(0, Math.min(100, bestAttempt.percentage || 0))}%`,
                                      backgroundColor: '#7a62a8',
                                    }}
                                  />
                                </div>
                                <small className="text-muted">
                                  Best score: {Math.round(bestAttempt.percentage || 0)}%
                                  {' · '}{attemptsForWeek(item.week).length} attempt{attemptsForWeek(item.week).length === 1 ? '' : 's'}
                                </small>
                              </>
                            ) : (
                              <small className="text-muted">MCQ quiz available</small>
                            )}
                          </>
                        ) : (
                          <small className="text-muted">Coming soon</small>
                        )}
                      </div>

                      <div className="col-12 col-sm-auto">
                        {ready ? (
                          user ? (
                            <Link
                              to={quizUrl}
                              state={{ quizName: `ML Foundations — Week ${item.week}: ${item.topic}` }}
                              className="btn btn-sm w-100"
                              style={{
                                color: '#7a62a8',
                                border: '1px solid #7a62a8',
                                borderRadius: 8,
                                minWidth: 150,
                              }}
                            >
                              <i className={`bi ${bestAttempt ? 'bi-arrow-clockwise' : 'bi-play-fill'} me-1`} aria-hidden="true"></i>
                              {bestAttempt ? 'Retake MCQ Quiz' : 'Take MCQ Quiz'}
                            </Link>
                          ) : (
                            <Link to="/login" className="btn btn-sm btn-outline-secondary w-100" style={{ minWidth: 150 }}>
                              <i className="bi bi-lock me-1" aria-hidden="true"></i>Log in to take quiz
                            </Link>
                          )
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary w-100"
                            disabled
                            style={{ minWidth: 150 }}
                          >
                            <i className="bi bi-clock me-1" aria-hidden="true"></i>Coming soon
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              )
            })}
            {Array.from({ length: TOTAL_WEEKS - weeks.length }, (_, index) => {
              const week = weeks.length + index + 1
              return (
                <article className="card border-0 shadow-sm mb-3" key={week} style={{ borderRadius: 14 }}>
                  <div className="card-body p-3 p-md-4">
                    <div className="row align-items-center g-3">
                      <div className="col-auto">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                          style={{ width: 44, height: 44, background: '#e9ecef', color: '#6c757d' }}
                        >
                          {week}
                        </div>
                      </div>
                      <div className="col">
                        <h2 className="h6 fw-semibold mb-1">Week {week}</h2>
                        <small className="text-muted">Course content will be available soon</small>
                      </div>
                      <div className="col-12 col-sm-auto">
                        <button type="button" className="btn btn-sm btn-outline-secondary w-100" disabled style={{ minWidth: 150 }}>
                          <i className="bi bi-clock me-1" aria-hidden="true"></i>Coming soon
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      </section>
    </main>
  )
}

export default MLFoundations
